import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import type { S3FileItem } from '@/types/transferFile';

export interface FileActionsProps {
  file: S3FileItem;
  onDownload: () => void;
  onDelete: () => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * FileActions Component
 *
 * - Files: download + delete (delete moves file from active to archive prefix)
 * - S3FileItem records are always files (folders are virtual, not stored as records)
 */
const FileActions: React.FC<FileActionsProps> = ({
  file,
  onDownload,
  onDelete,
  disabled = false,
  loading = false,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // S3FileItem records are always files (folders are virtual)
  const canDownload = true;
  const canDelete = true;

  const deleteTooltip = 'Delete file';

  const handleDownloadClick = () => {
    if (!disabled && !loading && canDownload) onDownload();
  };

  const handleDeleteClick = () => {
    if (!disabled && !loading && canDelete) setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    onDelete();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Download File">
          <span>
            <IconButton
              onClick={handleDownloadClick}
              disabled={disabled || loading || !canDownload}
              size="small"
              aria-label={`Download ${file.name}`}
            >
              {loading ? <CircularProgress size={16} /> : <DownloadIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={deleteTooltip}>
          <span>
            <IconButton
              onClick={handleDeleteClick}
              disabled={disabled || loading || !canDelete}
              size="small"
              color="warning"
              aria-label={`Delete ${file.name}`}
            >
              {loading && canDelete ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {canDelete && (
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete &quot;{file.name}&quot;?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="warning"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default FileActions;
